import React from "react";
import { Upload, Camera, X } from "lucide-react";

/**
 * Room photo upload area — drag/drop + click to browse, with preview & reset.
 * Extracted from DoozyGoMagic.js for readability.
 */
const MagicUploader = ({
  uploadedImage,
  fileRef,
  handleFileSelect,
  setUploadedFile,
  setUploadedImage,
  setResult,
  setError,
  handleReset,
}) => {
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      if (file.size > 10 * 1024 * 1024) { setError("Image too large (max 10 MB)"); return; }
      setUploadedFile(file);
      setUploadedImage(URL.createObjectURL(file));
      setResult(null); setError("");
    } else {
      setError("Please drop an image file (JPG/PNG)");
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-[#1E1B4B]">1. Upload Your Room Photo</h2>
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
        className={`relative rounded-2xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
          uploadedImage ? 'border-[#E83E8C]/40 bg-white' : 'border-gray-300 hover:border-[#E83E8C]/60 bg-white/60 hover:bg-[#E83E8C]/5'
        }`}
        style={{ minHeight: 260 }}
        data-testid="magic-upload-area"
      >
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="magic-file-input" />
        {uploadedImage ? (
          <div className="relative">
            <img src={uploadedImage} alt="Your room" className="w-full max-h-[350px] object-contain" />
            <button
              onClick={(e) => { e.stopPropagation(); handleReset(); }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all"
              data-testid="magic-upload-reset"
            >
              <X size={14} />
            </button>
            <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium flex items-center gap-1">
              <Camera size={12} /> Your Room
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#E83E8C]/10 flex items-center justify-center mb-3">
              <Upload size={28} className="text-[#E83E8C]" />
            </div>
            <p className="text-sm font-semibold text-[#1E1B4B] mb-1">Drop your room photo here</p>
            <p className="text-xs text-[#94a3b8]">or click to browse (JPG, PNG up to 10MB)</p>
          </div>
        )}
      </div>
      <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100">
        <p className="text-xs font-semibold text-indigo-800 mb-1">Tips for best results:</p>
        <ul className="text-xs text-indigo-700/80 space-y-0.5">
          <li>- Take the photo in landscape/wide angle</li>
          <li>- Good lighting helps the AI understand your space</li>
          <li>- Clear the area you want decorated</li>
        </ul>
      </div>
    </div>
  );
};

export default MagicUploader;
